// src/hooks/useSectorSlotLoader.js
import { supabase } from "../utils/supabaseClient";
import { normalizeSectorName } from "../utils/normalizeSectorName";
import { resolveRegionCode } from "../utils/resolveRegionCode";

export async function loadSectorSlots(sectorRaw, regionRaw, toast) {
  const isDev = import.meta.env.DEV;

  const sector = normalizeSectorName(sectorRaw);
  if (!sector) return { flyerId: null, slots: [] };

  const region = resolveRegionCode(sector);

  if (isDev) {
    console.log("[loadSectorSlots] 🔍 Sector:", sector);
    console.log("[loadSectorSlots] 🔍 Region:", region);
  }

  /* -------------------------------------------------------
     1️⃣ Load newest flyer for this sector
  -------------------------------------------------------- */
  const { data: flyerRow, error: flyerError } = await supabase
    .from("flyers")
    .select("id, region, postcode_sector, status, season, year")
    .eq("postcode_sector", sector)
    .order("year", { ascending: false })
    .order("season", { ascending: false })
    .limit(1)
    .single();

  if (flyerError || !flyerRow) {
    if (isDev) console.warn("[loadSectorSlots] ❌ No flyer found for:", sector);
    return { flyerId: null, slots: [] };
  }

  const flyerId = flyerRow.id;

  if (isDev) console.log("[loadSectorSlots] 📄 Flyer found:", flyerId);

  /* -------------------------------------------------------
     2️⃣ Load slots for this flyer
     (status is the ONLY source of truth)
  -------------------------------------------------------- */
  const { data: slots, error: slotsError } = await supabase
    .from("flyer_slots")
    .select(`
      id,
      slot_number,
      status,
      business_name,
      logo_url,
      qr_code_url,
      offer_title,
      offer_text
    `)
    .eq("flyer_id", flyerId)
    .order("slot_number", { ascending: true });

  if (slotsError) {
    console.error("[loadSectorSlots] ❌ Slot fetch error:", slotsError);
    toast?.({
      title: "Failed to load slots",
      description: slotsError.message || "Unknown error.",
      status: "error",
    });
    return { flyerId, slots: [] };
  }

  /* -------------------------------------------------------
     3️⃣ Build full 32-slot grid WITHOUT overwriting real data
  -------------------------------------------------------- */
  const fullGrid = Array.from({ length: 32 }, (_, i) => {
    const slotNum = i + 1;
    const existing = slots.find((s) => s.slot_number === slotNum);

    if (existing) {
      return {
        ...existing,
        placeholder: false,
      };
    }

    // Only create placeholder when slot truly does not exist
    return {
      id: `placeholder-${slotNum}`,
      slot_number: slotNum,
      status: "available",
      business_name: "",
      logo_url: "",
      qr_code_url: "",
      offer_title: "",
      offer_text: "",
      placeholder: true,
    };
  });

  if (isDev) console.log("[loadSectorSlots] ✅ Final slot grid ready");

  return { flyerId, slots: fullGrid };
}
